/**
 * Utility functions for creating URL-friendly slugs with encrypted job IDs
 */

import { encryptJobId, decryptJobId, isEncryptedJobId, computeShortIdFromUUID } from './encryption';

/**
 * Convert text to URL-friendly slug
 * @param {string} text - Text to slugify
 * @returns {string} - URL-friendly slug
 */
export const slugify = (text) => {
  if (!text) return '';
  
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

/**
 * Create a job slug combining title and short ID
 * @param {string} jobTitle - Job title
 * @param {string} jobId - Job UUID
 * @returns {string} - Slug in format: job-title-shortId
 */
export const createJobSlug = (jobTitle, jobId) => {
  if (!jobTitle || !jobId) return jobId || '';
  
  const slug = slugify(jobTitle);
  const shortId = computeShortIdFromUUID(jobId);
  return `${slug}-${shortId}`;
};

/**
 * Extract job ID from slug with short ID
 * @param {string} slug - Slug containing short job ID
 * @returns {string|null} - Short job ID for lookup or null
 */
export const extractJobIdFromSlug = (slug) => {
  if (!slug) return null;
  
  const parts = slug.split('-');
  
  // Try to find short ID in the last parts
  for (let i = parts.length - 1; i >= 0; i--) {
    const possibleId = parts.slice(i).join('-');
    
    // Check if it's a short ID (6-8 characters, hexadecimal)
    // This handles both full 8-char IDs and truncated 6-char IDs
    if (possibleId.length >= 6 && possibleId.length <= 8 && /^[0-9a-f]+$/i.test(possibleId)) {
      return possibleId.toLowerCase();
    }
    
    // Check if it looks like an encrypted ID (backward compatibility)
    if (isEncryptedJobId(possibleId)) {
      const decryptedId = decryptJobId(possibleId);
      if (decryptedId) {
        return decryptedId;
      }
    }
    
    // Fallback: check if it's a raw UUID (for backward compatibility)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(possibleId)) {
      return possibleId;
    }
  }
  
  return null;
};

/**
 * Get readable title from slug with encrypted ID
 * @param {string} slug - Full slug with encrypted ID
 * @returns {string} - Human readable title
 */
export const getTitleFromSlug = (slug) => {
  if (!slug) return '';
  
  const parts = slug.split('-');
  
  // Find the encrypted ID part and remove it
  let titleParts = [];
  for (let i = 0; i < parts.length; i++) {
    const possibleEncryptedId = parts.slice(i).join('-');
    const lastPart = parts[i];
    
    // Check if this part is a short ID (6-8 characters, hexadecimal)
    // This handles both full 8-char IDs and truncated 6-7 char IDs
    if (lastPart.length >= 6 && lastPart.length <= 8 && /^[0-9a-f]+$/i.test(lastPart)) {
      titleParts = parts.slice(0, i);
      break;
    }
    
    // If this looks like an encrypted ID, take everything before it
    if (isEncryptedJobId(possibleEncryptedId)) {
      titleParts = parts.slice(0, i);
      break;
    }
    
    // Fallback: check for raw UUID (backward compatibility)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(possibleEncryptedId)) {
      titleParts = parts.slice(0, i);
      break;
    }
  }
  
  // If no ID found, use all parts
  if (titleParts.length === 0) {
    titleParts = parts;
  }
  
  return titleParts
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
